import axios, { AxiosResponse, isAxiosError } from 'axios';
import { API_DOMAIN, FIRST_TIME_URL, LOGIN_URL, RECOVERY_CONFIRM_URL, RECOVERY_URL } from 'config/constants';
import { checkStatus } from './helpers';
import { getAuthDevice } from './authDevice';

import { handleTokenExpiredOrServerDown } from 'components/Router/hooks/useSetTokenExpired';

const { version } = require('../../package.json');

export type RangeMode = '$gte' | '$lte';

export type Range = {
	mode: RangeMode;
	value: number;
};

const api = axios.create({ baseURL: API_DOMAIN });

// A separate Axios instance without interceptors for specific use cases
const apiNoInterceptor = axios.create({ baseURL: API_DOMAIN });
const refreshApi = axios.create({ baseURL: API_DOMAIN });

api.defaults.headers.common['app-version'] = `${version}`;
api.defaults.headers.common['app-device'] = 'back-office';

apiNoInterceptor.defaults.headers.common['app-version'] = `${version}`;
apiNoInterceptor.defaults.headers.common['app-device'] = 'back-office';

refreshApi.defaults.headers.common['app-version'] = `${version}`;
refreshApi.defaults.headers.common['app-device'] = 'back-office';

/**
 * Set the received JWT token
 *
 * @param {string} AUTH_TOKEN
 */
function setToken(AUTH_TOKEN: string | null) {
	if (AUTH_TOKEN) {
		api.defaults.headers.common['Authorization'] = `Bearer ${AUTH_TOKEN}`;
		apiNoInterceptor.defaults.headers.common['Authorization'] = `Bearer ${AUTH_TOKEN}`;
		return;
	}
	delete api.defaults.headers.common['Authorization'];
	delete apiNoInterceptor.defaults.headers.common['Authorization'];
}

type LoginPostData = { username: string; password: string } | { email: string; password: string };

type LoginResponseData = {
	user: User;
	token: string;
	refreshToken: string;
	expires: string | number;
};

type LoginPostResponse = AxiosResponse<LoginResponseData>;

let refreshPromise: Promise<LoginResponseData> | null = null;

function readStoredSession(): Session | null {
	try {
		const stored = localStorage.getItem('session');
		return stored ? (JSON.parse(stored) as Session) : null;
	} catch {
		return null;
	}
}

function isLoginResponseData(data: unknown): data is LoginResponseData {
	if (!data || typeof data !== 'object') {
		return false;
	}
	const response = data as Partial<LoginResponseData>;
	return (
		typeof response.token === 'string' &&
		typeof response.refreshToken === 'string' &&
		['string', 'number'].includes(typeof response.expires) &&
		!!response.user
	);
}

async function refreshAccessToken(): Promise<LoginResponseData> {
	if (refreshPromise) {
		return refreshPromise;
	}

	refreshPromise = (async () => {
		const session = readStoredSession();
		const username = session?.user?.username;
		if (!session?.refreshToken || !username) {
			throw new Error('refresh token not found');
		}

		const response = await refreshApi.post<LoginResponseData>('/auth/refresh', {
			username,
			refreshToken: session.refreshToken,
		});
		if (!isLoginResponseData(response.data)) {
			throw new Error('invalid refresh response');
		}

		const refreshedSession: Session = {
			...session,
			...response.data,
			logged: true,
			expired: false,
			serverDown: false,
		};
		localStorage.setItem('session', JSON.stringify(refreshedSession));
		setToken(response.data.token);
		return response.data;
	})();

	try {
		return await refreshPromise;
	} finally {
		refreshPromise = null;
	}
}

// Add an interceptor to handle 401 || 404 errors
api.interceptors.response.use(
	(response) => response,
	async (error) => {
		if (isAxiosError(error) && error.response?.status === 401 && error.config) {
			const originalRequest = error.config as typeof error.config & { _retry?: boolean };
			const requestUrl = `${originalRequest.url || ''}`;
			const canRefresh =
				!originalRequest._retry && !requestUrl.includes(LOGIN_URL) && !requestUrl.includes('/auth/refresh');

			if (canRefresh) {
				originalRequest._retry = true;
				try {
					const refreshed = await refreshAccessToken();
					originalRequest.headers.Authorization = `Bearer ${refreshed.token}`;
					return api(originalRequest);
				} catch {
					// Existing session handler clears local state and redirects to login.
				}
			}
		}
		if (
			isAxiosError(error) &&
			(error.code === 'ERR_NETWORK' || error.response?.status === 404 || error.response?.status === 401)
		) {
			handleTokenExpiredOrServerDown();
		}
		return Promise.reject(error);
	}
);

type RecoveryConfirmResponse = AxiosResponse<{ success?: boolean }>;

// Since smaug 0.11.0 the recovery request no longer returns the token: the code
// and the link are only delivered by email, to avoid user enumeration.
type RecoveryPostResponse = AxiosResponse<{ success?: boolean }>;

type RecoveryConfirmPost = { code: string; password: string } & ({ email: string } | { username: string });

async function login(postData: LoginPostData) {
	const response = await api.post<LoginPostData & { device: string }, LoginPostResponse>(LOGIN_URL, {
		...postData,
		device: getAuthDevice(),
	});
	if (!checkStatus(response)) {
		throw new Error('invalid credentials');
	}
	if (!isLoginResponseData(response.data)) {
		throw Error('invalid login');
	}
	return response.data;
}

export async function recovery(postData: LoginPostData) {
	const response = await api.post<LoginPostData, RecoveryPostResponse>(RECOVERY_URL, postData);
	if (!checkStatus(response)) {
		throw new Error('invalid credentials');
	}
	if (!response.data.success) {
		throw Error('invalid recovery request');
	}
	return response.data;
}

export async function recoveryConfirm(postData: RecoveryConfirmPost) {
	const response = await api.post<RecoveryConfirmPost & { device: string }, RecoveryConfirmResponse>(
		RECOVERY_CONFIRM_URL,
		{...postData, device: getAuthDevice()}
	);
	if (!checkStatus(response)) {
		throw new Error('invalid credentials');
	}
	if (!response.data.success) {
		throw Error('invalid login');
	}
	return response.data;
}

async function isFirstTime() {
	const response = await api.get(FIRST_TIME_URL);
	if (!checkStatus(response) || typeof response.data.isFirstTime !== 'boolean') {
		return false;
	}
	return response.data.isFirstTime;
}

export async function importDataRequest<T>(url: string, file: File): Promise<ParseCsvFileResult<T>> {
	const formData = new FormData();
	formData.append('file', file);

	const response = await api.post<ParseCsvFileResult<T>>(url, formData, {
		headers: { 'Content-Type': 'multipart/form-data' },
	});

	if (!checkStatus(response)) {
		throw new Error('invalid credentials');
	}

	return response.data;
}

export { api, isFirstTime, login, setToken };
